'use client';

import {
  Collapsible,
  Flex,
  GridItem,
  Heading,
  Image,
  SimpleGrid,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import CountdownTimer from '@features/prediction-markets/CountdownTimer';
import { allPredictionMarkets, formatVolume } from '@features/prediction-markets/fakeMarketData';
import OutcomeRow, { YesNo } from '@features/prediction-markets/OutcomeRow';
import OutcomeRowExpanded from '@features/prediction-markets/OutcomeRowExpanded';
import PriceRangeProbabilityChart from '@features/prediction-markets/PriceRangeProbabilityChart';
import TradeDrawer from '@features/prediction-markets/TradeDrawer';
import TradePanel from '@features/prediction-markets/TradePanel';
import { MarketOutcome, PredictionMarket } from '@features/prediction-markets/types';
import { useBreadcrumbLabel } from '@features/navigation/BreadcrumbContext';
import { useHref } from 'hooks/useHref';
import Link from 'next/link';
import { memo, useCallback, useMemo, useState } from 'react';

// --- MarketDetailHeader ---

interface HeaderProps {
  market: PredictionMarket;
}

const MarketDetailHeader = memo(({ market }: HeaderProps) => {
  const { exploreHref, commodityHref } = useHref();

  const formattedDate = useMemo(() => {
    return new Date(market.endDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [market.endDate]);

  return (
    <>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={3}
      >
        <Flex alignItems="center" gap={3}>
          <Image
            src={market.commodityImage}
            alt={market.commodityName}
            boxSize="48px"
            borderRadius="full"
          />
          <Flex direction="column">
            <Text fontSize="xs" color="text.secondary">
              <Link href={exploreHref('prediction-markets')} style={{ textDecoration: 'none' }}>
                <Text as="span" _hover={{ textDecoration: 'underline' }}>
                  Commodities
                </Text>
              </Link>{' '}
              &middot;{' '}
              <Link href={commodityHref(market.commodityId)} style={{ textDecoration: 'none' }}>
                <Text as="span" _hover={{ textDecoration: 'underline' }}>
                  {market.commodityName}
                </Text>
              </Link>
            </Text>
            <Heading size="md">{market.question}</Heading>
          </Flex>
        </Flex>
        <CountdownTimer endDate={market.endDate} />
      </Flex>

      <Flex alignItems="center" gap={4}>
        <Text fontSize="sm" color="text.secondary">
          {formatVolume(market.totalVolume)} Vol.
        </Text>
        <Text fontSize="sm" color="text.secondary">
          ⏱ {formattedDate}
        </Text>
      </Flex>
    </>
  );
});
MarketDetailHeader.displayName = 'MarketDetailHeader';

// --- OutcomeItem (wraps Collapsible.Root to avoid inline handler) ---

interface OutcomeItemProps {
  outcome: MarketOutcome;
  market: PredictionMarket;
  isOpen: boolean;
  isSelectedOutcome: boolean;
  selectedYesNo: YesNo;
  onToggle: (outcomeId: string) => void;
  onSelectOutcome: (outcome: MarketOutcome, yesNo: YesNo) => void;
}

const OutcomeItem = memo(
  ({
    outcome,
    market,
    isOpen,
    isSelectedOutcome,
    selectedYesNo,
    onToggle,
    onSelectOutcome,
  }: OutcomeItemProps) => {
    const handleOpenChange = useCallback(() => {
      onToggle(outcome.id);
    }, [outcome.id, onToggle]);

    return (
      <Collapsible.Root open={isOpen} onOpenChange={handleOpenChange} lazyMount unmountOnExit>
        <OutcomeRow
          outcome={outcome}
          isExpanded={isOpen}
          isSelectedOutcome={isSelectedOutcome}
          selectedYesNo={selectedYesNo}
          onSelectOutcome={onSelectOutcome}
        />
        <OutcomeRowExpanded market={market} outcome={outcome} />
      </Collapsible.Root>
    );
  },
);
OutcomeItem.displayName = 'OutcomeItem';

// --- MarketDetailPage ---

interface Props {
  marketId: string;
}

const MarketDetailPage = ({ marketId }: Props) => {
  const market = allPredictionMarkets.find((m) => m.id === marketId);

  useBreadcrumbLabel('Market');

  const [expandedOutcomeId, setExpandedOutcomeId] = useState<string | null>(null);
  const isPriceRange = market?.questionType === 'weekly-price-range';
  const sortedOutcomes = useMemo(
    () => [...(market?.outcomes ?? [])].sort((a, b) => b.probability - a.probability),
    [market?.outcomes],
  );

  const [selectedOutcome, setSelectedOutcome] = useState<MarketOutcome | null>(
    sortedOutcomes[0] ?? null,
  );
  const [selectedYesNo, setSelectedYesNo] = useState<YesNo>('yes');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isLargeScreen = useBreakpointValue({ base: false, lg: true }, { fallback: 'lg' });

  const handleToggle = useCallback((outcomeId: string) => {
    setExpandedOutcomeId((prev) => (prev === outcomeId ? null : outcomeId));
  }, []);

  const handleSelectOutcome = useCallback((outcome: MarketOutcome, yesNo: YesNo) => {
    setSelectedOutcome(outcome);
    setSelectedYesNo(yesNo);
    setDrawerOpen(true);
  }, []);

  const handleYesNoChange = useCallback((value: YesNo) => {
    setSelectedYesNo(value);
  }, []);

  if (market == null) {
    return (
      <Flex p={4}>
        <Text color="text.secondary">Market not found.</Text>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={5} p={4}>
      <SimpleGrid columns={{ base: 1, lg: 5, xl: 4 }} gap={6}>
        <GridItem colSpan={{ base: 1, lg: 3 }}>
          <Flex direction="column" gap={4}>
            <MarketDetailHeader market={market} />

            {isPriceRange && <PriceRangeProbabilityChart market={market} />}

            <Flex direction="column" gap={2}>
              {sortedOutcomes.map((outcome) => (
                <OutcomeItem
                  key={outcome.id}
                  outcome={outcome}
                  market={market}
                  isOpen={expandedOutcomeId === outcome.id}
                  isSelectedOutcome={selectedOutcome?.id === outcome.id}
                  selectedYesNo={selectedYesNo}
                  onToggle={handleToggle}
                  onSelectOutcome={handleSelectOutcome}
                />
              ))}
            </Flex>
          </Flex>
        </GridItem>

        <GridItem colSpan={{ base: 1, lg: 2, xl: 1 }} hideBelow="lg">
          <TradePanel
            outcome={selectedOutcome}
            commodityImage={market.commodityImage}
            yesNo={selectedYesNo}
            onYesNoChange={handleYesNoChange}
          />
        </GridItem>
      </SimpleGrid>

      {!isLargeScreen && (
        <TradeDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          outcome={selectedOutcome}
          commodityImage={market.commodityImage}
          question={market.question}
          yesNo={selectedYesNo}
          onYesNoChange={handleYesNoChange}
        />
      )}
    </Flex>
  );
};

export default memo(MarketDetailPage);
